import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { siteBannerAPI } from '../services/api';
import { twMerge } from 'tailwind-merge';
import { unwrapResponse } from '../utils/unwrapResponse';
import { IconButton } from './ui';
import { queryKeys } from '../hooks/queries/queryKeys';

interface Banner {
  id: number;
  message: string;
  bannerType: 'info' | 'warning' | 'danger';
  linkUrl?: string;
  linkText?: string;
  dismissible: boolean;
  expiresAt?: string;
}

const DISMISSED_KEY = 'dismissedBannerId';

const bannerTypeClasses: Record<Banner['bannerType'], string> = {
  info: 'bg-info/10 text-info border-b border-info/20 dark:bg-[var(--theme-banner-info-bg)] dark:text-[var(--theme-banner-info-text)] dark:border-b-[var(--theme-banner-info-border)]',
  warning: 'bg-warning/10 text-warning border-b border-warning/20 dark:bg-[var(--theme-banner-warning-bg)] dark:text-[var(--theme-banner-warning-text)] dark:border-b-[var(--theme-banner-warning-border)]',
  danger: 'bg-danger/10 text-danger border-b border-danger/20 dark:bg-[var(--theme-banner-danger-bg)] dark:text-[var(--theme-banner-danger-text)] dark:border-b-[var(--theme-banner-danger-border)]',
};

const SiteBanner: React.FC = () => {
  // Snapshot at mount, matching the pre-migration effect's one-time
  // localStorage check — a banner dismissed for a DIFFERENT id doesn't
  // suppress a newer one, and this only ever updates via handleDismiss.
  const [dismissedId, setDismissedId] = useState<string | null>(
    () => localStorage.getItem(DISMISSED_KEY)
  );

  const { data: banner } = useQuery<Banner | null>({
    queryKey: queryKeys.siteBanner.active(),
    queryFn: async () => {
      const response = await siteBannerAPI.getActive();
      return unwrapResponse<Banner | null>(response.data, null);
    },
    // Pre-migration code was a single try/catch'd fetch with no retry
    // wrapper, and failures were silently swallowed (banner is
    // non-critical) -- matched here by retry:false + rendering nothing
    // on error (query.data stays undefined).
    retry: false,
  });

  const visibleBanner: Banner | null = banner && dismissedId !== String(banner.id) ? banner : null;

  if (!visibleBanner) return null;

  const handleDismiss = () => {
    localStorage.setItem(DISMISSED_KEY, String(visibleBanner.id));
    setDismissedId(String(visibleBanner.id));
  };

  return (
    <div
      className={twMerge('w-full py-2.5 px-4 text-center text-sm leading-snug z-[100]', bannerTypeClasses[visibleBanner.bannerType])}
      role="alert"
    >
      <div className="max-w-[1200px] mx-auto flex items-center justify-center gap-3">
        <span className="flex-1">
          {visibleBanner.message}
          {visibleBanner.linkUrl && (
            <>
              {' '}
              <a
                href={visibleBanner.linkUrl}
                className="text-current underline font-semibold hover:opacity-80"
                target="_blank"
                rel="noopener noreferrer"
              >
                {visibleBanner.linkText || 'Learn more'}
              </a>
            </>
          )}
        </span>
        {visibleBanner.dismissible && (
          <IconButton
            icon={<span aria-hidden="true">&times;</span>}
            aria-label="Dismiss banner"
            variant="ghost"
            size="sm"
            onClick={handleDismiss}
            className="shrink-0"
          />
        )}
      </div>
    </div>
  );
};

export default SiteBanner;
